import { layerNaturalSize } from '../core/model/bounds';
import { getLayer, patchMask, removeMask, setMask, updateLayer } from '../core/model/document';
import { canHaveMask } from '../core/model/mask';
import type { Layer, Rect, Size } from '../core/model/types';
import { bakeMaskIntoLayerUrl, createMaskUrl, maskFromRectUrl } from '../io/mask';
import type { AppContext } from './context';
import { refuseMultiSelection } from './context';
import { beginDocumentEdit, STALE_EDIT_MESSAGE } from './staleGuard';

/**
 * Layer mask flows, each committed as one history entry.
 *
 * The split is the usual one: `core/model/mask.ts` holds the maths, `io/mask.ts`
 * moves pixels across the canvas boundary, and this file decides *when* — what
 * the menu items mean, what is refused, and what the user is told.
 *
 * The rule that makes masks safe to live with: a mask never touches the layer's
 * pixels. Adding, inverting, disabling or removing one rewrites only the mask, so
 * a mask can be abandoned as cheaply as it was made. Only `Apply Mask` bakes, and
 * it is a command the user asks for by name.
 */

/**
 * How a new mask is made. Named apart from `core/model/mask`'s `MaskMode`, which is
 * the two solid masks only — 'selection' is a flow, not a pixel pattern.
 */
export type MaskAddMode = 'reveal' | 'hide' | 'selection';

export type MaskRejection =
  | 'no-layer'
  | 'unsupported'
  | 'group'
  | 'locked'
  | 'no-mask'
  | 'no-selection'
  | 'already-applying';

const REJECTION_MESSAGES: Record<MaskRejection, string> = {
  'no-layer': 'Masks need an image or paint layer.',
  unsupported: 'That layer type cannot be masked yet — rasterise it first.',
  group: 'A group cannot be masked — mask the layers inside it.',
  locked: 'That layer is locked.',
  'no-mask': 'That layer has no mask.',
  'no-selection': 'Add a mask from a selection needs a selection first.',
  'already-applying': 'A mask is already being applied.',
};

export function maskRejectionMessage(reason: MaskRejection): string {
  return REJECTION_MESSAGES[reason];
}

export interface MaskTarget {
  layer: Layer;
  size: Size;
}

/** The layer mask commands act on, or why they cannot. */
export function maskTarget(
  ctx: AppContext,
  layerId?: string | null,
): { target: MaskTarget | null; reason?: MaskRejection } {
  const { doc, activeLayerId } = ctx.store.getState();
  const id = layerId === undefined ? activeLayerId : layerId;
  const layer = id ? getLayer(doc, id) : undefined;
  if (!layer) return { target: null, reason: 'no-layer' };
  // A group has no pixels of its own, so it has no pixel space to cut a
  // stencil in. Mask the layers inside it instead.
  if (layer.type === 'group') return { target: null, reason: 'group' };
  if (!canHaveMask(layer)) return { target: null, reason: 'unsupported' };
  if (layer.locked) return { target: null, reason: 'locked' };
  // The mask lives in the layer's own pixel space — for a paint layer that is
  // its stored raster size, which a crop can differ from the document by.
  const size = layerNaturalSize(layer);
  if (!size) return { target: null, reason: 'unsupported' };
  return { target: { layer, size } };
}

/** A mask that shows everything, hides everything, or shows the selection. */
export function addMask(ctx: AppContext, mode: MaskAddMode = 'reveal'): void {
  // A mask is a per-layer stencil; "mask three layers at once" is a different
  // feature, not a rider on this one (design doc §4).
  if (refuseMultiSelection(ctx, 'Masks work on one layer — select just it')) return;
  const { doc, selection, commit, notify } = ctx.store.getState();
  const { target, reason } = maskTarget(ctx);
  if (!target) {
    ctx.notify(maskRejectionMessage(reason ?? 'no-layer'), 'warn');
    return;
  }
  const { layer, size } = target;

  let src: string;
  if (mode === 'selection') {
    if (!selection) {
      ctx.notify(maskRejectionMessage('no-selection'), 'warn');
      return;
    }
    src = maskFromRectUrl(toLayerSpace(layer, selection), size);
  } else {
    src = createMaskUrl(size, mode);
  }

  const replaced = Boolean(layer.mask?.src);
  commit(
    replaced ? 'Replace Mask' : 'Add Mask',
    setMask(doc, layer.id, { src, enabled: true, inverted: false }),
  );
  notify(
    mode === 'selection'
      ? 'Mask added from the selection'
      : mode === 'hide'
        ? 'Mask added, hiding everything'
        : 'Mask added, revealing everything',
  );
}

/** Remove the mask entirely. The layer's pixels were never touched, so this is free. */
export function removeActiveMask(ctx: AppContext): void {
  if (refuseMultiSelection(ctx, 'Masks work on one layer — select just it')) return;
  const { doc, commit, notify } = ctx.store.getState();
  const { target, reason } = maskTarget(ctx);
  if (!target) {
    ctx.notify(maskRejectionMessage(reason ?? 'no-layer'), 'warn');
    return;
  }
  if (!target.layer.mask) {
    ctx.notify(maskRejectionMessage('no-mask'), 'warn');
    return;
  }
  commit('Remove Mask', removeMask(doc, target.layer.id));
  ctx.store.getState().setMaskEditing(null);
  notify('Mask removed');
}

/**
 * Flip coverage: hidden becomes shown and vice versa.
 *
 * This flips a *flag* rather than the pixels. It costs nothing, it is lossless, and
 * inverting twice is exactly a no-op — which a pixel inversion only approximates,
 * because PNG round-tripping a mask twice can move a value by one.
 */
export function invertActiveMask(ctx: AppContext): void {
  if (refuseMultiSelection(ctx, 'Masks work on one layer — select just it')) return;
  const { doc, commit, notify } = ctx.store.getState();
  const { target, reason } = maskTarget(ctx);
  if (!target) {
    ctx.notify(maskRejectionMessage(reason ?? 'no-layer'), 'warn');
    return;
  }
  const mask = target.layer.mask;
  if (!mask) {
    ctx.notify(maskRejectionMessage('no-mask'), 'warn');
    return;
  }
  commit('Invert Mask', patchMask(doc, target.layer.id, { inverted: !mask.inverted }));
  notify('Mask inverted');
}

/** Switch a mask off without throwing it away. */
export function toggleMaskEnabled(ctx: AppContext): void {
  if (refuseMultiSelection(ctx, 'Masks work on one layer — select just it')) return;
  const { doc, commit, notify } = ctx.store.getState();
  const { target, reason } = maskTarget(ctx);
  const mask = target?.layer.mask;
  if (!target || !mask) {
    ctx.notify(maskRejectionMessage(target ? 'no-mask' : (reason ?? 'no-layer')), 'warn');
    return;
  }
  commit(
    mask.enabled ? 'Disable Mask' : 'Enable Mask',
    patchMask(doc, target.layer.id, {
      enabled: !mask.enabled,
    }),
  );
  notify(mask.enabled ? 'Mask disabled' : 'Mask enabled');
}

/**
 * Paint on the mask instead of the layer.
 *
 * The view stays composited rather than switching to a grey mask view: painting
 * the mask then shows exactly what the stroke does to the picture, which is the
 * thing people find surprising when an editor shows them the stencil instead.
 */
export function toggleMaskEditing(ctx: AppContext): void {
  if (refuseMultiSelection(ctx, 'Masks work on one layer — select just it')) return;
  const { doc, maskEditing, setMaskEditing, notify } = ctx.store.getState();
  const { target, reason } = maskTarget(ctx);
  if (!target) {
    ctx.notify(maskRejectionMessage(reason ?? 'no-layer'), 'warn');
    return;
  }
  // Clicking the mask you are already painting is how you stop; clicking another
  // layer's mask switches to it rather than doing nothing.
  if (maskEditing === target.layer.id) {
    setMaskEditing(null);
    notify('Painting the layer again');
    return;
  }
  const mask = target.layer.mask;
  if (!mask) {
    ctx.notify(maskRejectionMessage('no-mask'), 'warn');
    return;
  }
  if (!mask.enabled) {
    // Editing a disabled mask would paint into something with no visible effect,
    // which reads as a broken brush. Switch it on as part of entering.
    ctx.store.getState().commit('Enable Mask', patchMask(doc, target.layer.id, { enabled: true }));
  }
  setMaskEditing(target.layer.id);
  ctx.store.getState().setRightPanel('layers');
  notify(`Editing the mask of “${target.layer.name}” — Esc to leave`);
}

export function leaveMaskEditing(ctx: AppContext): void {
  if (!ctx.store.getState().maskEditing) return;
  ctx.store.getState().setMaskEditing(null);
  ctx.notify('Painting the layer again');
}

/**
 * Bake the mask into the layer and drop it.
 *
 * Destructive by definition — the pixels are resampled into the mask's alpha — so
 * it is opt-in, one history entry, and the mask is removed on the way so the layer
 * cannot end up masked by the mask that just became it.
 */
export async function applyMaskToLayer(ctx: AppContext): Promise<void> {
  if (refuseMultiSelection(ctx, 'Masks work on one layer — select just it')) return;
  const { doc, commit, notify } = ctx.store.getState();
  const { target, reason } = maskTarget(ctx);
  if (!target) {
    ctx.notify(maskRejectionMessage(reason ?? 'no-layer'), 'warn');
    return;
  }
  const mask = target.layer.mask;
  if (!mask?.src) {
    ctx.notify(maskRejectionMessage('no-mask'), 'warn');
    return;
  }
  const layer = target.layer;
  const src = layer.type === 'image' ? layer.src : layer.type === 'paint' ? layer.pixels : '';
  if (!src) {
    ctx.notify('That layer has no pixels to apply a mask to.', 'warn');
    return;
  }
  const documentStale = beginDocumentEdit(ctx);
  try {
    const pixels = await bakeMaskIntoLayerUrl(src, target.size, mask.src, mask.inverted);
    // The bake read a captured snapshot; a commit that landed inside the await
    // would be reverted by the one below.
    if (documentStale()) {
      ctx.notify(STALE_EDIT_MESSAGE, 'warn');
      return;
    }
    const next =
      layer.type === 'image'
        ? updateLayer(doc, layer.id, { src: pixels, mask: null })
        : updateLayer(doc, layer.id, { pixels, mask: null });
    ctx.store.getState().setMaskEditing(null);
    commit('Apply Mask', next);
    notify('Mask applied to the layer');
  } catch (error) {
    ctx.notify(error instanceof Error ? error.message : 'That mask could not be applied.', 'error');
  }
}

/** A document-space rect, expressed in the layer's own pixel space. */
export function toLayerSpace(layer: Layer, rect: Rect): Rect {
  return {
    x: (rect.x - layer.x) / (layer.scaleX || 1),
    y: (rect.y - layer.y) / (layer.scaleY || 1),
    width: rect.width / (layer.scaleX || 1),
    height: rect.height / (layer.scaleY || 1),
  };
}
